import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { Autocomplete, Box, Chip, FormControl, TextField } from '@mui/material';
import { ConfigEntryType } from 'config/config';
import { USStates } from 'constants/USStates';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';

type ProjectNotesStateFieldProps = {
  field: ConfigEntryType;
};
type OptionType = { name: string; value: string };

function ProjectNotesStateField({ field }: ProjectNotesStateFieldProps) {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const {
    control,
    formState: { isSubmitting },
    getValues,
    setValue,
  } = useFormContext();
  const selectedOptions: string[] = useWatch({
    control,
    name: field?.entryCode,
    defaultValue: [],
  });
  const defaultSelectedOptions = useMemo(() => {
    return selectedOptions?.map?.((item) => ({ name: item, value: item })) || [];
  }, []);
  const [fieldValue, setFieldValue] = useState<OptionType[]>(defaultSelectedOptions);

  const handleSelect = (_: unknown, values: (string | OptionType)[]) => {
    let selectedValues: string[] = [];
    let fieldValues: OptionType[] = [];
    values.map((item) => {
      if (typeof item === 'string') {
        selectedValues.push(item);
        fieldValues.push({ name: item, value: item });
      } else {
        selectedValues.push(item?.value);
        fieldValues.push(item);
      }
    });
    setValue(field.entryCode, selectedValues, { shouldDirty: true, shouldValidate: true });
    setFieldValue(fieldValues);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (USStates?.length && selectedOptions.length) {
      setFieldValue(USStates.filter((item) => selectedOptions.includes(item.value)));
    } else {
      setFieldValue([]);
    }
  }, [USStates, selectedOptions]);

  return (
    <>
      <Controller
        control={control}
        render={({ fieldState, field: { ref } }) => (
          <FormControl fullWidth>
            <Autocomplete
              id={field.entryCode}
              {...(field?.referenceObject?.freeText && {
                freeSolo: true,
                open,
                onOpen: (event) => {
                  const currentValue = (event.target as HTMLInputElement).value;
                  if (currentValue?.trim()) {
                    setOpen(true);
                  }
                },
                onClose: () => setOpen(false),
                onKeyDown: handleKeyDown,
              })}
              multiple
              limitTags={3}
              disableCloseOnSelect
              inputValue={inputValue}
              onInputChange={(_, value) => {
                setInputValue(value);
                if (!value && open && !!field?.referenceObject?.freeText) {
                  setOpen(false);
                }
              }}
              onChange={handleSelect}
              options={USStates}
              autoHighlight
              value={fieldValue}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              noOptionsText={'No Data Found'}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option?.name ?? '';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  // placeholder={`${field.referenceObject?.placeHolder || 'Select'}`}
                  variant="standard"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={option.name}
                    deleteIcon={<CloseLineIcon size={20} />}
                  />
                ))
              }
              disabled={isSubmitting}
            />
          </FormControl>
        )}
        name={`${field.entryCode}`}
      />
    </>
  );
}

export default ProjectNotesStateField;
